import React, { useState, useEffect } from 'react';
import logoImage from '../assets/images/rasmoose_logo.png';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { 
    FaSignOutAlt, FaMapMarkedAlt, FaComments, FaUserCheck, 
    FaHeart, FaArrowRight, FaQuestionCircle
} from 'react-icons/fa';
import InfoTooltip from '../components/InfoTooltip'; // Import the new component

import '../styles/LandingPage.css';

// Reusable animation variants
const sectionVariants = {
  hidden: { opacity: 0, y: 50 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: 'easeOut' } }
};

// Animated Section Wrapper
const AnimatedSection = ({ children, className }) => {
  const controls = useAnimation();
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  useEffect(() => {
    if (inView) {
      controls.start('visible');
    }
  }, [controls, inView]);

  return (
    <motion.section 
      ref={ref}
      initial="hidden"
      animate={controls}
      variants={sectionVariants}
      className={`landing-section ${className || ''}`}
    >
      {children}
    </motion.section>
  );
};


function LandingPage() {
    const { currentUser, logout, isLoading } = useAuth();
    const [isScrolled, setIsScrolled] = useState(false);
    const [images] = useState([
        'https://res.cloudinary.com/dtgxn7ux7/image/upload/xbqogwzimbkgprucltfb.jpg',
        'https://res.cloudinary.com/dtgxn7ux7/image/upload/wclcy94ow7eyjoieur7k.jpg',
        'https://res.cloudinary.com/dtgxn7ux7/image/upload/qsfksgnck4fxuka7drce.jpg',
        'https://res.cloudinary.com/dtgxn7ux7/image/upload/luyinyvgjn3stf2y0tr0.jpg',
        'https://res.cloudinary.com/dtgxn7ux7/image/upload/sfoijdwar0sbcydyd0co.jpg',
        'https://res.cloudinary.com/dtgxn7ux7/image/upload/v1755197634/srf4splisuahnqwlufsf.jpg'
    ]);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);

    useEffect(() => {
        const intervalId = setInterval(() => {
            setCurrentImageIndex(prevIndex => (prevIndex + 1) % images.length);
        }, 5000); // Change image every 5 seconds

        return () => clearInterval(intervalId);
    }, [images.length]);

    useEffect(() => {
        const handleScroll = () => setIsScrolled(window.scrollY > 10);
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const cities = [
        { name: 'Warsaw', image: 'https://res.cloudinary.com/dtgxn7ux7/image/upload/yvnn9w1elhoa2hpaohcv.jpg' },
        { name: 'Kuopio', image: 'https://res.cloudinary.com/dtgxn7ux7/image/upload/myget2qqee76xtp6e991.jpg' },
    ];

    const features = [
        { icon: FaMapMarkedAlt, title: "Curated Student Maps", text: "Find the best spots, from hidden cafes to essential offices, all recommended by students who've been there." },
        { icon: FaUserCheck, title: "Verified Reviews", text: "Trust every review. Our verification system ensures all content is from real, active students." },
        { icon: FaComments, title: "Connect & Chat", text: "Join city-wide group chats to ask questions, share tips, and meet fellow exchange students." },
        { icon: FaHeart, title: "Save Your Favorites", text: "Build your personal go-to list of locations and never forget a recommendation." },
    ];

    return (
        <div className="landing-page-v3">
            {/* Header */}
            <header className={`lp-header ${isScrolled ? 'scrolled' : ''}`}>
                <div className="lp-container">
                    <div className="logo">
                        <img src={logoImage} alt="Rasmoose logo" className="logo-image" />
                        <span>Rasmoose</span>
                    </div>
                    <div className="auth-actions">
                        {isLoading ? (
                            <div className="loader-small"></div>
                        ) : currentUser ? (
                            <div className="user-display">
                                <span className="welcome-text">
                                    Welcome, {currentUser.displayName || 'Student'}
                                </span>
                                <div className="verify-container">
                                    <Link to="/verify" className={`verify-badge ${currentUser.is_verified_student ? 'verified' : ''}`}>
                                        {currentUser.is_verified_student ? 'Verified 🎓' : 'Get Verified'}
                                    </Link>
                                    {!currentUser.is_verified_student && (
                                        <InfoTooltip 
                                            text="Verification with a student email is required to post reviews and use the chat. This helps us keep the community authentic!"
                                        >
                                            <div className="tooltip-trigger">
                                                <FaQuestionCircle />
                                            </div>
                                        </InfoTooltip>
                                    )}
                                </div>
                                <button onClick={logout} className="logout-button-v3" aria-label="Sign out">
                                    <FaSignOutAlt />
                                </button>
                            </div>
                        ) : (
                            <Link to="/auth" className="auth-button">
                                Sign In / Register
                            </Link>
                        )}
                    </div>
                </div>
            </header>

            <main>
                {/* Hero Section */}
                <section className="hero-section landing-page-hero">
                    <div className="hero-background">
                        {images.map((image, index) => (
                            <img
                                key={index}
                                src={image}
                                alt="City landscape"
                                className={`hero-bg-image ${index === currentImageIndex ? 'active' : ''}`}
                            />
                        ))}
                    </div>
                    <div className="hero-overlay"></div>
                    <div className="lp-container">
                        <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2 }}>
                            Your Exchange, Reimagined.
                        </motion.h1>
                        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.4 }}>
                            Navigate your new city with confidence using maps and guides curated by students, for students.
                        </motion.p>
                        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.6 }}>
                            <Link to={currentUser ? `/city/warsaw` : "/auth"} className="cta-button">
                                Explore a City <FaArrowRight />
                            </Link>
                        </motion.div>
                    </div>
                </section>

                {/* Cities Section */}
                <AnimatedSection className="cities-section">
                    <div className="lp-container">
                        <h2>Start Your Adventure</h2>
                        <div className="city-grid">
                            {cities.map(city => (
                                <Link to={`/city/${city.name.toLowerCase()}`} key={city.name} className="city-card-link">
                                    <div className="city-card">
                                        <img src={city.image} alt={city.name} className="city-image" />
                                        <div className="city-card-overlay">
                                            <h3>{city.name}</h3>
                                            <div className="explore-button">
                                                Explore <FaArrowRight />
                                            </div>
                                        </div>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>
                </AnimatedSection>
                
                {/* Features Section */}
                <AnimatedSection className="features-section">
                    <div className="lp-container">
                        <h2>Why Rasmoose?</h2>
                        <div className="features-grid">
                            {features.map((feature, index) => {
                                const Icon = feature.icon;
                                return (
                                <div className="feature-card-v3" key={index}>
                                    <div className="feature-icon-v3"><Icon/></div>
                                    <div className="feature-text-v3">
                                        <h3>{feature.title}</h3>
                                        <p>{feature.text}</p>
                                    </div>
                                </div>
                                );
                            })}
                        </div>
                    </div>
                </AnimatedSection>
            </main>

            {/* Footer */}
            <footer className="lp-footer">
                <div className="lp-container">
                    <div className="footer-content">
                        <div className="footer-brand">
                            <img src={logoImage} alt="Rasmoose logo" className="logo-image-footer" />
                            <p>&copy; {new Date().getFullYear()} Rasmoose. All Rights Reserved.</p>
                        </div>
                        <div className="footer-links">
                            <Link to="/privacy">Privacy Policy</Link>
                            <Link to="/terms">Terms of Service</Link>
                            <a href="https://github.com" target="_blank" rel="noopener noreferrer">GitHub</a>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
}

export default LandingPage;